import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { getVideoCallUsage } from '../../utils/videoCallDatabase';
import { checkVideoCallPackage } from '../../utils/videoCallUtils';

const VideoCallSettings = ({ storeId, packageType }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Settings state
  const [enabled, setEnabled] = useState(false);
  const [scheduleType, setScheduleType] = useState('global'); // 'global', 'product_specific', 'flexible'
  const [globalSchedule, setGlobalSchedule] = useState({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: []
  });
  
  // Advanced settings
  const [maxConcurrentCalls, setMaxConcurrentCalls] = useState(2);
  const [callDuration, setCallDuration] = useState(20);
  const [bufferTime, setBufferTime] = useState(15);
  const [autoAccept, setAutoAccept] = useState(false);
  const [maxRequestsPerDay, setMaxRequestsPerDay] = useState(10);
  const [storeUsage, setStoreUsage] = useState(null);
  const [storePackageInfo, setStorePackageInfo] = useState(null);

  // Product-specific schedules
  const [productSchedules, setProductSchedules] = useState({});
  
  // Flexible daily schedules
  const [dailySchedules, setDailySchedules] = useState({});

  useEffect(() => {
    if (storeId) {
      loadSettings();
      loadStoreUsage();
    }
  }, [storeId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const scheduleRef = doc(db, 'videoCallSchedules', storeId);
      const scheduleSnap = await getDoc(scheduleRef);
      
      if (scheduleSnap.exists()) {
        const data = scheduleSnap.data();
        setEnabled(data.isActive || false);
        setScheduleType(data.type || 'global');
        setGlobalSchedule(data.schedule || {
          monday: [], tuesday: [], wednesday: [], thursday: [],
          friday: [], saturday: [], sunday: []
        });
        setProductSchedules(data.productSchedules || {});
        setDailySchedules(data.dailySchedules || {});
        setMaxConcurrentCalls(data.maxConcurrentCalls || 2);
        setCallDuration(data.callDuration || 20);
        setBufferTime(data.bufferTime || 15);
        setAutoAccept(data.autoAccept || false);
        setMaxRequestsPerDay(data.maxRequestsPerDay || 10);
      }
    } catch (error) {
      console.error('Error loading video call settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStoreUsage = async () => {
    try {
      if (!storeId || !currentUser) return;
      
      // Load store package info
      const packageInfo = await checkVideoCallPackage(currentUser.uid);
      setStorePackageInfo(packageInfo);
      
      // Load usage
      const usage = await getVideoCallUsage(storeId, false);
      setStoreUsage(usage);
    } catch (error) {
      console.error('Error loading store usage:', error);
    }
  };

  // Quick setup: ตั้งค่าเวลาเริ่มต้น (Business Hours)
  const setupDefaultSchedule = () => {
    const defaultSchedule = {
      monday: [{ start: '09:00', end: '18:00' }],
      tuesday: [{ start: '09:00', end: '18:00' }],
      wednesday: [{ start: '09:00', end: '18:00' }],
      thursday: [{ start: '09:00', end: '18:00' }],
      friday: [{ start: '09:00', end: '18:00' }],
      saturday: [{ start: '09:00', end: '18:00' }],
      sunday: [{ start: '09:00', end: '18:00' }]
    };
    setGlobalSchedule(defaultSchedule);
    setEnabled(true);
    setScheduleType('global');
  };

  const handleSave = async () => {
    if (!storeId || !currentUser) return;
    
    // Validate: ถ้าเปิดใช้งาน ต้องมี schedule อย่างน้อย 1 วัน
    if (enabled && scheduleType === 'global') {
      const hasSchedule = Object.values(globalSchedule).some(day => day && day.length > 0);
      if (!hasSchedule) {
        alert('กรุณากำหนดเวลา Video Call อย่างน้อย 1 วัน หรือใช้ Quick Setup');
        return;
      }
    }
    
    try {
      setSaving(true);
      const scheduleRef = doc(db, 'videoCallSchedules', storeId);
      
      const scheduleData = {
        storeId,
        type: scheduleType,
        schedule: globalSchedule,
        productSchedules: productSchedules,
        dailySchedules: dailySchedules,
        maxConcurrentCalls,
        callDuration,
        bufferTime,
        autoAccept,
        maxRequestsPerDay,
        isActive: enabled,
        updatedAt: new Date()
      };
      
      await setDoc(scheduleRef, scheduleData, { merge: true });
      alert(t('videoCall.settingsSaved'));
    } catch (error) {
      console.error('Error saving video call settings:', error);
      alert('เกิดข้อผิดพลาดในการบันทึก: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const addTimeSlot = (day) => {
    setGlobalSchedule(prev => ({
      ...prev,
      [day]: [...prev[day], { start: '10:00', end: '12:00' }]
    }));
  };

  const removeTimeSlot = (day, index) => {
    setGlobalSchedule(prev => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== index)
    }));
  };

  const updateTimeSlot = (day, index, field, value) => {
    setGlobalSchedule(prev => ({
      ...prev,
      [day]: prev[day].map((slot, i) => 
        i === index ? { ...slot, [field]: value } : slot
      )
    }));
  };

  const days = [
    { key: 'monday', label: 'จันทร์' },
    { key: 'tuesday', label: 'อังคาร' },
    { key: 'wednesday', label: 'พุธ' },
    { key: 'thursday', label: 'พฤหัสบดี' },
    { key: 'friday', label: 'ศุกร์' },
    { key: 'saturday', label: 'เสาร์' },
    { key: 'sunday', label: 'อาทิตย์' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
      </div>
    );
  }

  // Check if package supports video call
  const hasVideoCallSupport = packageType && packageType !== 'free';

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm md:shadow-md p-3 md:p-4 border border-gray-100">
        <h2 className="text-lg md:text-2xl font-bold text-gray-800 flex items-center gap-2">
          <span className="text-xl md:text-2xl">📹</span>
          <span>Video Call Settings</span>
        </h2>
      </div>

      {!hasVideoCallSupport && (
        <div className="bg-white rounded-lg shadow-sm md:shadow-md p-3 md:p-4 border border-yellow-200 bg-yellow-50">
          <p className="text-xs md:text-sm text-yellow-800">
            ⚠️ Video Call requires Basic Package or higher. 
            <a href="#package" className="text-blue-600 underline ml-1">Upgrade now</a>
          </p>
        </div>
      )}

      {/* Store Usage Info Card */}
      {hasVideoCallSupport && storePackageInfo && storeUsage && (
        <div className="bg-white rounded-lg shadow-sm md:shadow-md p-3 md:p-4 border border-gray-100">
          <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-3">{t('videoCall.storeUsage')}</h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs md:text-sm text-gray-700 mb-1">
                {t('videoCall.package')}: <span className="font-bold text-blue-600">{storePackageInfo.packageType?.toUpperCase() || 'N/A'}</span>
              </p>
              {storePackageInfo.callsLimit === -1 ? (
                <p className="text-xs md:text-sm text-gray-600">♾️ {t('videoCall.unlimitedCalls')}</p>
              ) : (
                <p className="text-xs md:text-sm text-gray-600">
                  {t('videoCall.used', { used: storeUsage.totalCalls || 0, limit: storePackageInfo.callsLimit })}
                </p>
              )}
            </div>
            {storePackageInfo.callsLimit !== -1 && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs md:text-sm text-gray-700">
                    {t('videoCall.remaining')}: <span className="font-bold text-green-600">{storePackageInfo.callsRemaining}</span>
                  </p>
                  <span className="text-xs text-gray-500">
                    {Math.round(((storeUsage.totalCalls || 0) / storePackageInfo.callsLimit) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(((storeUsage.totalCalls || 0) / storePackageInfo.callsLimit) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Enable/Disable Card */}
      <div className="bg-white rounded-lg shadow-sm md:shadow-md p-3 md:p-4 border border-gray-100">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={!hasVideoCallSupport}
            className="w-5 h-5 mt-0.5 text-green-500 rounded focus:ring-green-500 flex-shrink-0"
          />
          <div className="flex-1">
            <span className="text-sm md:text-base font-semibold text-gray-800 block">
              Enable Video Call for Store
            </span>
            {enabled && (
              <p className="text-xs md:text-sm text-gray-600 mt-1.5">
                Video Call จะแสดงในหน้า Product Detail สำหรับสินค้าทั้งหมด
              </p>
            )}
          </div>
        </label>
      </div>

      {enabled && (
        <>
          {/* Schedule Type Card */}
          <div className="bg-white rounded-lg shadow-sm md:shadow-md p-3 md:p-4 border border-gray-100">
            <label className="block text-sm md:text-base font-semibold text-gray-800 mb-3">
              Schedule Type
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="scheduleType"
                  value="global"
                  checked={scheduleType === 'global'}
                  onChange={(e) => setScheduleType(e.target.value)}
                  className="w-4 h-4 text-green-500"
                />
                <span className="text-xs md:text-sm text-gray-700">Global (ทั้งร้าน)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="scheduleType"
                  value="product_specific"
                  checked={scheduleType === 'product_specific'}
                  onChange={(e) => setScheduleType(e.target.value)}
                  className="w-4 h-4 text-green-500"
                />
                <span className="text-xs md:text-sm text-gray-700">Per Product (เฉพาะสินค้า)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="scheduleType"
                  value="flexible"
                  checked={scheduleType === 'flexible'}
                  onChange={(e) => setScheduleType(e.target.value)}
                  className="w-4 h-4 text-green-500"
                />
                <span className="text-xs md:text-sm text-gray-700">Flexible (กำหนดรายวัน)</span>
              </label>
            </div>
          </div>

          {/* Global Schedule Card */}
          {scheduleType === 'global' && (
            <div className="bg-white rounded-lg shadow-sm md:shadow-md p-3 md:p-4 border border-gray-100">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <h3 className="text-sm md:text-base font-semibold text-gray-800">Weekly Schedule</h3>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={setupDefaultSchedule}
                    className="text-xs md:text-sm text-green-600 hover:text-green-700 font-semibold px-3 py-1.5 bg-green-50 rounded-lg transition-colors"
                  >
                    ⚡ Quick Setup (9:00-18:00)
                  </button>
                  <button
                    onClick={() => {
                      const defaultSchedule = { start: '10:00', end: '12:00' };
                      const newSchedule = {};
                      days.forEach(day => {
                        newSchedule[day.key] = [defaultSchedule];
                      });
                      setGlobalSchedule(newSchedule);
                    }}
                    className="text-xs md:text-sm text-blue-600 hover:text-blue-700 px-3 py-1.5 bg-blue-50 rounded-lg transition-colors"
                  >
                    Set All Days
                  </button>
                </div>
              </div>

              {days.map(day => (
                <div key={day.key} className="mb-3 pb-3 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs md:text-sm font-medium text-gray-700">{day.label}</label>
                    <button
                      onClick={() => addTimeSlot(day.key)}
                      className="text-xs md:text-sm text-green-600 hover:text-green-700 font-medium px-2 py-1 bg-green-50 rounded transition-colors"
                    >
                      + เพิ่มเวลา
                    </button>
                  </div>
                  
                  {globalSchedule[day.key]?.map((slot, index) => (
                    <div key={index} className="flex items-center gap-2 mb-2 p-2 bg-gray-50 rounded-lg">
                      <input
                        type="time"
                        value={slot.start}
                        onChange={(e) => updateTimeSlot(day.key, index, 'start', e.target.value)}
                        className="flex-1 px-2 py-1.5 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                      <span className="text-gray-500 text-xs">-</span>
                      <input
                        type="time"
                        value={slot.end}
                        onChange={(e) => updateTimeSlot(day.key, index, 'end', e.target.value)}
                        className="flex-1 px-2 py-1.5 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                      <button
                        onClick={() => removeTimeSlot(day.key, index)}
                        className="px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        ลบ
                      </button>
                    </div>
                  ))}
                  
                  {(!globalSchedule[day.key] || globalSchedule[day.key].length === 0) && (
                    <p className="text-xs text-gray-400 italic">ยังไม่มีการกำหนดเวลา</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Product-Specific Schedule Card */}
          {scheduleType === 'product_specific' && (
            <div className="bg-white rounded-lg shadow-sm md:shadow-md p-3 md:p-4 border border-gray-100">
              <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-2">Product-Specific Schedule</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-3">
                เลือกสินค้าและกำหนดเวลา Video Call สำหรับแต่ละสินค้า
              </p>
              <button className="px-3 py-2 text-xs md:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                + Add Product Schedule
              </button>
              <p className="text-xs text-gray-500 mt-3">
                ⚠️ Feature นี้จะเพิ่มใน Phase 2
              </p>
            </div>
          )}

          {/* Flexible Daily Schedule Card */}
          {scheduleType === 'flexible' && (
            <div className="bg-white rounded-lg shadow-sm md:shadow-md p-3 md:p-4 border border-gray-100">
              <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-2">Flexible Daily Schedule</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-3">
                กำหนดเวลา Video Call แบบรายวัน
              </p>
              <button className="px-3 py-2 text-xs md:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                + Add Daily Schedule
              </button>
              <p className="text-xs text-gray-500 mt-3">
                ⚠️ Feature นี้จะเพิ่มใน Phase 2
              </p>
            </div>
          )}

          {/* Advanced Settings Card */}
          <div className="bg-white rounded-lg shadow-sm md:shadow-md p-3 md:p-4 border border-gray-100">
            <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-3">Advanced Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                  Max Concurrent Calls
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={maxConcurrentCalls}
                  onChange={(e) => setMaxConcurrentCalls(parseInt(e.target.value))}
                  className="w-full px-3 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">จำนวน Video Call ที่สามารถทำพร้อมกันได้</p>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                  Call Duration (minutes)
                </label>
                <input
                  type="number"
                  min="5"
                  max="60"
                  value={callDuration}
                  onChange={(e) => setCallDuration(parseInt(e.target.value))}
                  className="w-full px-3 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">ระยะเวลาสูงสุดของแต่ละ Video Call</p>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                  Buffer Time (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  max="60"
                  value={bufferTime}
                  onChange={(e) => setBufferTime(parseInt(e.target.value))}
                  className="w-full px-3 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">เวลาระหว่าง Video Call แต่ละครั้ง</p>
              </div>

              <div className="p-2 bg-gray-50 rounded-lg">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoAccept}
                    onChange={(e) => setAutoAccept(e.target.checked)}
                    className="w-4 h-4 mt-0.5 text-green-500 rounded flex-shrink-0"
                  />
                  <div className="flex-1">
                    <span className="text-xs md:text-sm font-medium text-gray-700 block">
                      Auto-Accept Requests
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      อนุมัติ Video Call requests อัตโนมัติถ้าอยู่ในเวลาที่กำหนด
                    </p>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                  Max Requests per Day
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={maxRequestsPerDay}
                  onChange={(e) => setMaxRequestsPerDay(parseInt(e.target.value))}
                  className="w-full px-3 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">จำนวน requests สูงสุดต่อวัน</p>
              </div>
            </div>
          </div>

          {/* Save Button Card */}
          <div className="bg-white rounded-lg shadow-sm md:shadow-md p-3 md:p-4 border border-gray-100">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full px-4 py-2.5 md:py-3 bg-green-500 text-white text-sm md:text-base font-semibold rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
            >
              {saving ? t('common.saving') : t('videoCall.saveSettings')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default VideoCallSettings;

